import * as tslib_1 from "tslib";
import { Repeat } from '../../domain/helpers/repeat';
import * as moment from 'moment';
import { EntityState } from 'breeze-client';
import { AbstractBase } from '../../core/helpers/abstract-base';
/**
 * This adapter bridges the interface repeat entity towards the way we represent it in the GUI.
 */
var RepeatAdapter = /** @class */ (function (_super) {
    tslib_1.__extends(RepeatAdapter, _super);
    /**
     * Constructs a RepeatAdapter.
     * @param provider Is used to get the current repeat.
     * @param translateService Is needed to get a translated summary.
     */
    function RepeatAdapter(provider, translateService, localisationService, domainService) {
        var _this = _super.call(this) || this;
        _this.provider = provider;
        _this.localisationService = localisationService;
        _this.domainService = domainService;
        if (!RepeatAdapter.labels) {
            _this.subscriptionPool.add(translateService.get([
                'APP.FORMS.REPEAT.ONCE',
                'APP.FORMS.REPEAT.DAILY',
                'APP.FORMS.REPEAT.WEEKDAY',
                'APP.FORMS.REPEAT.WEEKLY',
                'APP.FORMS.REPEAT.MONTHLY',
                'APP.FORMS.REPEAT.YEARLY',
                'APP.FORMS.REPEAT.WEEKDAYS',
                'APP.FORMS.REPEAT.FIRST',
                'APP.FORMS.REPEAT.SECOND',
                'APP.FORMS.REPEAT.THIRD',
                'APP.FORMS.REPEAT.FOURTH',
                'APP.FORMS.REPEAT.LAST',
                'APP.FORMS.REPEAT.EVERY',
                'APP.FORMS.REPEAT.DAYS',
                'APP.FORMS.REPEAT.WEEKS',
                'APP.FORMS.REPEAT.MONTHS',
                'APP.FORMS.REPEAT.YEARS',
                'APP.FORMS.REPEAT.ON',
                'APP.FORMS.REPEAT.OF',
                'APP.FORMS.REPEAT.ONTHE',
                'APP.FORMS.REPEAT.AND',
                'APP.FORMS.REPEAT.TIME',
                'APP.FORMS.REPEAT.TIMES',
                'APP.FORMS.REPEAT.TILL',
                'APP.FORMS.REPEAT.OR_LAST_DAY'
            ]).subscribe(function (value) {
                RepeatAdapter.labels = value;
            }));
        }
        return _this;
    }
    Object.defineProperty(RepeatAdapter.prototype, "repeat", {
        /** Gets the repeat entity to represent. */
        get: function () {
            return this.provider();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RepeatAdapter.prototype, "factor", {
        /** Gets the repeat factor. */
        get: function () {
            if (!this.repeat) {
                return 0;
            }
            return this.repeat.Factor;
        },
        /** Sets the repeat factor. */
        set: function (value) {
            if (!this.repeat) {
                return;
            }
            this.repeat.Factor = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RepeatAdapter.prototype, "order", {
        /** Gets the repeat order. */
        get: function () {
            if (!this.repeat) {
                return '';
            }
            return this.repeat.Order;
        },
        /** Sets the repeat order. */
        set: function (value) {
            if (!this.repeat) {
                return;
            }
            this.repeat.Order = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RepeatAdapter.prototype, "days", {
        /** Gets the days of the week in a weekly repeat. */
        get: function () {
            var _this = this;
            if (!this.repeat) {
                return '';
            }
            if (this.offsetType === 'ABSOLUTE') {
                var owner = this.repeat.Owner;
                if (owner) {
                    var start = owner['Start'];
                    var date_1 = start.getDate().toString();
                    if (date_1 !== this.repeat.Days) {
                        window.setTimeout(function () {
                            _this.repeat.Days = date_1;
                        });
                    }
                }
            }
            return this.repeat.Days;
        },
        /** Sets the days of the week in a weekly repeat. */
        set: function (value) {
            if (!this.repeat) {
                return;
            }
            this.repeat.Days = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RepeatAdapter.prototype, "endingIs", {
        /**
         * Gets the ending state of the repeat.
         * @returns NEVER when the repeat untilDate is infinit otherwise NEAR.
         */
        get: function () {
            if (this.isInfinity()) {
                return 'NEVER';
            }
            return 'NEAR';
        },
        /** Sets the ending state of the repeat. */
        set: function (value) {
            if (this.endingIs === value) {
                return;
            }
            switch (value) {
                case 'NEVER':
                    this.untilDate = this.makeInfinite();
                    break;
                case 'NEAR':
                    if (this.repeat) {
                        var PROPERTY_UNTILDATE = 'UntilDate';
                        var untilDateOriginal = this.repeat.entityAspect.originalValues[PROPERTY_UNTILDATE];
                        if (untilDateOriginal && untilDateOriginal.getUTCFullYear() < 9999) {
                            this.untilDate = untilDateOriginal;
                        }
                        else if (this.repeat.Owner) {
                            this.untilDate = this.repeat.Owner['Start'];
                        }
                        else {
                            this.untilDate = this.repeat.Start;
                        }
                    }
                    break;
                default:
                    throw new Error('Unknown ending value: ' + value);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RepeatAdapter.prototype, "untilDate", {
        /** Gets the repeat.untilDate propery. */
        get: function () {
            if (!this.repeat) {
                return this.makeInfinite();
            }
            return this.repeat.UntilDate;
        },
        /** Sets the repeat.untilDate propery. */
        set: function (value) {
            if (!this.repeat || !value || ((value.valueOf() - this.repeat.UntilDate.valueOf()) / 60000) === 0) {
                // Nothing to set or nothing really changed.
                return;
            }
            if (value.getUTCFullYear() === 9999) {
                // Assume we mean infinite. Time zone client can cause a difference of up to 27 hours.
                this.repeat.UntilDate = this.makeInfinite();
                return;
            }
            if (((value.valueOf() - this.repeat.UntilDate.valueOf()) / 60000) !== 0) {
                this.repeat.UntilDate = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RepeatAdapter.prototype, "repeatType", {
        /** Gets the repeat type of the repeat. */
        get: function () {
            if (!this.repeat) {
                return 'ONCE';
            }
            switch (this.repeat.Kind) {
                case 'MONTHLY ABSOLUTE':
                case 'MONTHLY RELATIVE':
                    return 'MONTHLY';
                case 'YEARLY ABSOLUTE':
                case 'YEARLY RELATIVE':
                    return 'YEARLY';
                case 'WEEKLY':
                    if (this.factor === 1 && this.repeat.Days) {
                        var days = this.repeat.Days.trim().split(' ');
                        if (days.length === 5 && days.indexOf('SUN') === -1 && days.indexOf('SAT') === -1) {
                            return 'WEEKDAY';
                        }
                    }
                    return 'WEEKLY';
                default:
                    return this.repeat.Kind;
            }
        },
        /** Sets the repeat type of the repeat. */
        set: function (value) {
            var _this = this;
            if (!this.repeat) {
                return;
            }
            if (this.repeatType === value) {
                return;
            }
            switch (value) {
                case 'YEARLY':
                case 'MONTHLY':
                    this.repeat.Kind = value + ' ABSOLUTE';
                    break;
                case 'WEEKDAY':
                    this.repeat.Kind = 'WEEKLY';
                    this.repeat.Days = 'MON TUE WED THU FRI';
                    this.factor = 1;
                    break;
                case 'WEEKLY':
                    this.repeat.Kind = 'WEEKLY';
                    this.repeat.Days = '';
                    break;
                default:
                    this.repeat.Kind = value;
            }
            if (this.repeat.entityAspect.entityState === EntityState.Modified &&
                this.repeat['Oid'].indexOf('\\') > 0 && this.repeatType !== 'ONCE') {
                var masterRef = this.repeat['Oid'].split('\\'); // Get the master reference.
                var masterType = this.domainService.getEntityType(masterRef[0]);
                this.subscriptionPool.add(this.domainService.getEntityByKey(masterType, masterRef[1], true).subscribe(function (item) {
                    var manager = _this.repeat.entityAspect.entityManager;
                    var master = item; // Get the master.
                    var repeat = _this.repeat; // this.domainService.copy(this.repeat); // Set repeat of subscription;
                    repeat.entityAspect.setAdded(); // Force the creation of a new repeat.
                    if (master.entityAspect.entityState === EntityState.Unchanged) {
                        master.entityAspect.setModified();
                    }
                    var subscription = manager.entityChanged.subscribe(function (data) {
                        if (data.entity === master) {
                            if (master.entityAspect.entityState === EntityState.Unchanged) { // The master is saved or canceled.
                                // Unsubscribe.
                                manager.entityChanged.unsubscribe(subscription);
                                if (repeat['Oid'].indexOf('\\') > 0) { // The master was canceled.
                                    // So repeat also reset it.
                                    repeat.Kind = 'ONCE';
                                    repeat.entityAspect.setUnchanged();
                                }
                                return;
                            }
                        }
                        if (data.entity === repeat) {
                            if (repeat.entityAspect.entityState === EntityState.Unchanged) { // repeat is saved.
                                // Unsubscribe.
                                manager.entityChanged.unsubscribe(subscription);
                                // Refresh master.
                                _this.domainService.getEntityByKey(master.entityType, master.entityAspect.getKey().values, false);
                            }
                        }
                    });
                }));
            }
            return;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RepeatAdapter.prototype, "offsetType", {
        /** Gets the offset type, 'RELATIVE' or 'ABSOLUTE', for a monthly or yearly repeat. */
        get: function () {
            if (!this.repeat) {
                return 'RELATIVE'; // Default value
            }
            switch (this.repeat.Kind) {
                case 'MONTHLY ABSOLUTE':
                case 'YEARLY ABSOLUTE':
                    return 'ABSOLUTE';
                case 'MONTHLY RELATIVE':
                case 'YEARLY RELATIVE':
                    return 'RELATIVE';
                default:
                    return 'RELATIVE'; // Default value
            }
        },
        /** Sets the offset type, 'RELATIVE' or 'ABSOLUTE', for a monthly or yearly repeat. */
        set: function (value) {
            if (!this.repeat) {
                return;
            }
            if (this.offsetType === value) {
                return;
            }
            this.repeat.Days = '';
            switch (this.repeatType) {
                case 'MONTHLY':
                    this.repeat.Kind = 'MONTHLY ' + value;
                    break;
                case 'YEARLY':
                    this.repeat.Kind = 'YEARLY ' + value;
                    break;
                default:
                    throw new Error('Unknown repeat type: ' + this.repeatType);
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Checks whether repeat.untilDate is infinite.
     * @returns True when repeat.untilDate is infinite.
     */
    RepeatAdapter.prototype.isInfinity = function () {
        return !this.untilDate || Repeat.isInfinity(this.untilDate);
    };
    /**
     * Returns a description of the repeat.
     * @returns a string representing the repeat.
     */
    RepeatAdapter.prototype.summary = function () {
        // In de constructor hier boven haal ik de labels asynchroon op.
        // Wanneer je nog meer labels nodig hebt, voeg dan
        // de label keys in de string array van de get toe.
        if (!RepeatAdapter.labels) {
            return '';
        }
        if (!this.repeat) {
            return RepeatAdapter.labels['APP.FORMS.REPEAT.ONCE'];
        }
        var recurrenceString = function (factor, freq, plural) {
            if (!factor || factor === 1) {
                return RepeatAdapter.labels['APP.FORMS.REPEAT.' + freq];
            }
            else {
                return RepeatAdapter.labels['APP.FORMS.REPEAT.EVERY'] + ' ' +
                    factor + ' ' +
                    RepeatAdapter.labels['APP.FORMS.REPEAT.' + plural];
            }
        };
        var daysString = function (offset, repeatDays, languageCode) {
            if (repeatDays === '') {
                return repeatDays;
            }
            var resultDays = '';
            var days = repeatDays.trim().split(' ');
            if (days.length === 5 && days.indexOf('SUN') === -1 && days.indexOf('SAT') === -1) {
                resultDays += RepeatAdapter.labels['APP.FORMS.REPEAT.WEEKDAYS'];
            }
            else {
                var daysArray_1 = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
                days.sort(function (a, b) {
                    return daysArray_1.indexOf(a) - daysArray_1.indexOf(b);
                });
                var index = days.indexOf(daysArray_1[offset.getDay()]);
                if (index !== -1 && index !== 0) {
                    var rotate = days.slice(0, index);
                    days.splice(0, index);
                    for (var day = 0; day < rotate.length; day += 1) {
                        days.push(rotate[day]);
                    }
                }
                for (var day = 0; day < days.length; day += 1) {
                    if (day > 0) {
                        resultDays += day === days.length - 1 ? ' ' + RepeatAdapter.labels['APP.FORMS.REPEAT.AND'] + ' ' : ', ';
                    }
                    var weekday = moment.weekdays(daysArray_1.indexOf(days[day]));
                    if (languageCode === 'nl') {
                        weekday = weekday.toLowerCase();
                    }
                    resultDays += weekday;
                }
            }
            return resultDays;
        };
        var result = '';
        switch (this.repeat.Kind) {
            case 'ONCE':
                return RepeatAdapter.labels['APP.FORMS.REPEAT.ONCE'];
            case 'DAILY':
                result = recurrenceString(this.repeat.Factor, 'DAILY', 'DAYS');
                break;
            case 'WEEKLY':
                if (this.repeatType === 'WEEKDAY') {
                    result = RepeatAdapter.labels['APP.FORMS.REPEAT.EVERY'] + ' ' +
                        this.localisationService.currentLanguage.code === 'de' ?
                        RepeatAdapter.labels['APP.FORMS.REPEAT.WEEKDAY'] :
                        RepeatAdapter.labels['APP.FORMS.REPEAT.WEEKDAY'].toLowerCase();
                    break;
                }
                if (!this.repeat.Days) {
                    result = RepeatAdapter.labels['APP.FORMS.REPEAT.WEEKLY'];
                    break;
                }
                result = RepeatAdapter.labels['APP.FORMS.REPEAT.EVERY'] + ' ';
                if (this.repeat.Factor && this.repeat.Factor !== 1) {
                    result += this.repeat.Factor + ' ' +
                        RepeatAdapter.labels['APP.FORMS.REPEAT.WEEKS'] + ' ' +
                        RepeatAdapter.labels['APP.FORMS.REPEAT.ON'] + ' ';
                }
                result += daysString(this.start(), this.repeat.Days, this.localisationService.currentLanguage.code);
                break;
            case 'MONTHLY ABSOLUTE':
                result = recurrenceString(this.repeat.Factor, 'MONTHLY', 'MONTHS');
                if (this.repeat.Days) {
                    // Month 0 is januari
                    var date = new Date(2000, 0, Number(this.repeat.Days));
                    result += ' ' + RepeatAdapter.labels['APP.FORMS.REPEAT.ONTHE'] + ' ' + moment(date).format('Do');
                    if (date.getDate() > 28) {
                        result += ' ' + RepeatAdapter.labels['APP.FORMS.REPEAT.OR_LAST_DAY'] + ' ';
                    }
                }
                break;
            case 'MONTHLY RELATIVE':
                result = recurrenceString(this.repeat.Factor, 'MONTHLY', 'MONTHS');
                if (this.repeat.Order) {
                    result += ' ' + RepeatAdapter.labels['APP.FORMS.REPEAT.ONTHE'] + ' ' +
                        RepeatAdapter.labels['APP.FORMS.REPEAT.' + this.repeat.Order] + ' ' +
                        daysString(this.start(), this.repeat.Days, this.localisationService.currentLanguage.code);
                }
                break;
            case 'YEARLY ABSOLUTE':
                result = recurrenceString(1, 'YEARLY', 'YEARS');
                if (this.repeat.Days && this.factor) {
                    var date = new Date(2000, this.factor - 1, Number(this.repeat.Days));
                    result += ' ' + RepeatAdapter.labels['APP.FORMS.REPEAT.ON'] + ' ' +
                        moment(date).format(this.localisationService.currentLanguage.formats.agenda.week.start);
                }
                break;
            case 'YEARLY RELATIVE':
                result = recurrenceString(1, 'YEARLY', 'YEARS');
                if (this.repeat.Order) {
                    result += ' ' + RepeatAdapter.labels['APP.FORMS.REPEAT.ONTHE'] + ' ' +
                        RepeatAdapter.labels['APP.FORMS.REPEAT.' + this.repeat.Order] + ' ' +
                        daysString(this.start(), this.repeat.Days, this.localisationService.currentLanguage.code);
                }
                // Oude code: if (this.repeat.Start && this.repeat.entityAspect.entityState !== EntityState.Added) {
                // Check op entity state weggehaalt van wegen issue:
                // https://github.com/Symagic/html5/issues/1051#issuecomment-395001908
                // Ik weet niet of ik hier iets anders stuk maak, Maarten?
                if (this.repeat.Start) {
                    var date = new Date(2000, this.factor - 1, 1);
                    result += ' ' + RepeatAdapter.labels['APP.FORMS.REPEAT.OF'] + ' ' + moment(date).format('MMMM');
                }
                break;
            default:
                if (this.repeat.Factor) {
                    result += RepeatAdapter.labels['APP.FORMS.REPEAT.EVERY'] + ' ' + this.repeat.Factor + ' ';
                }
                if (this.repeat.Days) {
                    result += daysString(this.start(), this.repeat.Days, this.localisationService.currentLanguage.code);
                }
        }
        if (!this.isInfinity()) {
            result += ' ' + RepeatAdapter.labels['APP.FORMS.REPEAT.TILL'] + ' ' +
                moment(this.untilDate).format(this.localisationService.currentLanguage.formats.date.format);
        }
        return result;
        // Aan de hand van de RepeatAdapter properties kan je hier de summary samenstellen.
        // Nu alleen nog maar de repeatType property gebruikt.
        // return RepeatAdapter.labels['APP.CORE.MODELS.REPEAT.' + this.repeatType];
    };
    /**
     * Makes date that represents the server side infinite date.
     * @private
     * @returns {Date} 9999/12/31 +01:00 independent of time-zone client.
     */
    RepeatAdapter.prototype.makeInfinite = function () {
        // Returns 9999/12/31 +01:00 independent of time-zone client.
        return new Date(Date.UTC(9999, 11, 30, 23));
    };
    RepeatAdapter.prototype.start = function () {
        if (this.repeat) {
            if (this.repeat.Owner) {
                return this.repeat.Owner['Start'];
            }
            if (this.repeat.Start) {
                return this.repeat.Start;
            }
        }
        return new Date();
    };
    RepeatAdapter.labels = undefined;
    return RepeatAdapter;
}(AbstractBase));
export { RepeatAdapter };
